import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import logo from "../../assets/logo-mark.png";
import { upcomingSchedule } from "../../data/schedule";

// lucide-react has no brand/social icons, so these are drawn inline (same marks used on the Contact page)
const FacebookIcon = () => (
  <svg className="h-[18px] w-[18px]" fill="currentColor" viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.99h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
  </svg>
);

const InstagramIcon = () => (
  <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const TwitterIcon = () => (
  <svg className="h-[18px] w-[18px]" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const socialLinks = [
  { icon: FacebookIcon, label: "Facebook", href: "https://facebook.com/csipkhomas" },
  { icon: InstagramIcon, label: "Instagram", href: "https://instagram.com/csipkhomas" },
  { icon: TwitterIcon, label: "Twitter", href: "https://twitter.com/csipkhomas" },
];

const Footer = () => {
  return (
    <footer className="bg-primary text-white">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-4">
              <img src={logo} alt="CSIP — Community Sanitation Improvement Programme" className="h-14 w-auto bg-white rounded px-2 py-1" />
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-4">
              Community Sanitation Improvement Programme — Khomas Region.
              Uniting residents for cleaner, healthier communities.
            </p>
            <div className="flex gap-3">
              {socialLinks.map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="text-white/60 hover:text-secondary transition-colors"
                >
                  <Icon />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display font-bold text-secondary uppercase tracking-wide mb-4">
              Quick Links
            </h4>
            <ul className="space-y-2 text-sm text-white/70">
              {[
                { label: "About CSIP", to: "/about" },
                { label: "Our Team", to: "/about/team" },
                { label: "Our Campaigns", to: "/campaigns" },
                { label: "Our Work", to: "/our-work" },
                { label: "Impact Stories", to: "/impact" },
                { label: "Resources", to: "/resources" },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-secondary transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Campaign Schedule */}
          <div>
            <h4 className="font-display font-bold text-secondary uppercase tracking-wide mb-4">
              2026 Campaign
            </h4>
            <ul className="space-y-1 text-sm text-white/70">
              {upcomingSchedule()
                .slice(0, 5)
                .map((s) => {
                  const [day, month] = s.date.split(" ");
                  const short = month ? `${day} ${month.slice(0, 3)}` : s.date;
                  return (
                    <li key={s.area} className="flex justify-between gap-2">
                      <span>{s.area}</span>
                      <span className="text-secondary font-medium">{short}</span>
                    </li>
                  );
                })}
              <li>
                <Link to="/campaigns" className="text-secondary hover:underline text-xs mt-1 block">
                  View full schedule →
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display font-bold text-secondary uppercase tracking-wide mb-4">
              Contact Us
            </h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-center gap-2">
                <Phone size={14} className="text-secondary shrink-0" />
                <a href="tel:+264812974558" className="hover:text-secondary transition-colors">
                  +264 81 297 4558
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={14} className="text-secondary shrink-0" />
                <a href="mailto:team@csipkohas.org" className="hover:text-secondary transition-colors">
                  team@csipkohas.org
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={14} className="text-secondary shrink-0 mt-0.5" />
                Windhoek, Khomas Region, Namibia
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-white/40">
          <p>© {new Date().getFullYear()} CSIP — Community Sanitation Improvement Programme, Khomas Region.</p>
          <p>All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
